/* eslint-disable no-useless-computed-key */
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { FaFileImage } from "react-icons/fa";
import { TbWorldWww } from "react-icons/tb";

import React, { useContext, useEffect, useState } from "react";
import { FaImage } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa6";
import { MdOutlineTextSnippet } from "react-icons/md";
import { fetchController } from "../../utils/FetchController/fetchController";
import { formatDateWithAmPm } from "../../utils/DateFormat/dateFormatWithTimeAmPm";
import Loader from "../../utils/Loader/Loader";
import PaginationSizeSelect from "../../utils/Loader/PaginationSizeSelect";
import Paginator from "../../utils/Loader/Paginator";
import { getHostDomain, getTagsData } from "../../utils/utils";
import AddDataSources from "../Modals/AddDataSource";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import Table from "../Tables/Table";
import DataSourceStatusIcon from "./DataSourceStateIcon";
import DatasourcesTour from "./DatasourcesTour";
import { DS_ICONS, HEADS_DATA } from "./constants";
import TagsColumn from "../Tags/TagsColumn";
import Layout from "../Layout/Layout";
import NameWithDescriptionCell from "../shared/NameWithDescriptionCell";

const timeAgo = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const utcStr = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
    return formatDistanceToNow(new Date(utcStr), { addSuffix: true });
  } catch {
    return "—";
  }
};

const IconComponent = ({ dsType }) => {
  const iconData = DS_ICONS[dsType];
  if (!iconData) return null;

  const IconTag = {
    FaFilePdf,
    TbWorldWww,
    FaImage,
    FaFileImage,
    MdOutlineTextSnippet,
  }[iconData.icon];

  return (
    <Box marginLeft={2}>
      <Icon as={IconTag} color={iconData.color} />
    </Box>
  );
};

const DataSources = () => {
  const baseUrl = getHostDomain();
  const [tableData, setTableData] = useState([]);
  const [heads, setHeads] = useState([]);
  const [status, setStatus] = useState("idle");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [paginationSize, setPaginationSize] = useState(10);

  const endpoint = `/api/v1/datasource/get_my_details?size=${paginationSize}&page=${page}`;
  const urlHit = baseUrl + endpoint;

  const hitFetch = async () => {
    try {
      setStatus("loading");
      const response = await fetchController(urlHit, "GET");
      const updatedData = response.data.items.map((item) => {
        const tagsData = getTagsData(item?.tags);

        return {
          Status: <DataSourceStatusIcon ds_status={item.ds_status} />,
          ...item,
          // Override `name` AFTER the spread so the cell renders the
          // shared name+description block instead of the raw string.
          name: (
            <NameWithDescriptionCell
              name={item.name}
              description={item.description}
              id={item.id}
            />
          ),
          Type: <IconComponent dsType={item.ds_type} />,
          assistants: (
            <Box ml={4}>{item.assistants_count.toLocaleString()}</Box>
          ),
          data_source_files: (
            <Box ml={4}>{item.data_source_files_count.toLocaleString()}</Box>
          ),
          word_count: (
            <Box ml={4}>{item.total_word_count.toLocaleString()}</Box>
          ),
          Created: item.created_at ? (
            <Tooltip label={formatDateWithAmPm(item.created_at, "short")}>
              <Text as="span" fontSize="sm" cursor="default">{timeAgo(item.created_at)}</Text>
            </Tooltip>
          ) : "—",
          Tags: <TagsColumn tagsData={tagsData} refreshTable={hitFetch} />,
        };
      });
      const columns = HEADS_DATA.map((key) => ({
        Header: key,
        accessor: key,
        Filter: ({ column }) => <input {...column.filterProps} />,
        Cell: ({ value }) => {
          if (Array.isArray(value)) {
            return <div style={{ marginLeft: "20px" }}>{value.length}</div>;
          }
          return value;
        },
      }));
      setTableData(updatedData);
      setHeads(columns);
      setPages(response?.data?.pages);
      setStatus("success");
    } catch (error) {
      setStatus("failure");
    }
  };
  useEffect(() => {
    hitFetch();
  }, [page, paginationSize]);

  const [datasourcesTourVisibility, setDatasourcesTourVisibility] = useState(
    localStorage.getItem("datasourcesTourVisibility") ? false : true
  );
  const onCloseDatasourcesTour = () => {
    localStorage.setItem("datasourcesTourVisibility", false);
    setDatasourcesTourVisibility(false);
  };
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme } = agency;

  const onGotoFirstPage = () => setPage(1);

  return (
    <>
      <VStack
        h="100%"
         w={"100%"}
        spacing={2}
        justifyContent={"space-between"}
        borderRadius="md"
      >
        <Flex direction={"column"} gap={3} h={"95%"} w={"100%"}>
          <Flex gap={2} w={"100%"} alignItems="center" p={2}>
            <Heading color={textColor} fontSize="xl">
              Data Sources
            </Heading>
            <Button
              colorScheme={buttonColorScheme}
              onClick={() => onOpen()}
              size={"sm"}
            >
              {"Create Data Source"}
            </Button>
            {datasourcesTourVisibility ? (
              <DatasourcesTour
                onCloseDatasourcesTour={onCloseDatasourcesTour}
              />
            ) : null}
          </Flex>

          {status === "loading" ? (
            <Loader />
          ) : (
            <Table
              columns={heads}
              data={tableData}
              table="data-sources"
              fetch={hitFetch}
            />
          )}
        </Flex>

        <Flex
          justifyContent={"flex-end"}
          alignItems={"center"}
          marginBlock={2}
          gap={2}
          w={"100%"}
        >
          <PaginationSizeSelect
            paginationSize={paginationSize}
            setPaginationSize={setPaginationSize}
            tableLength={tableData.length}
          />
          <Paginator
            colorScheme={buttonColorScheme}
            onChangePage={setPage}
            page={page}
            pages={pages}
          />
        </Flex>
      </VStack>

      <AddDataSources
        fetchSources={hitFetch}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        onGotoFirstPage={onGotoFirstPage}
      />
    </>
  );
};

export default DataSources;
